<template>
  <div
    class="vacancy-card relative h-full border focus-within:border"
    :class="[
      {
        'rounded-lg shadow-lg': cardStyle === 'raised',
        'overflow-hidden rounded-md shadow-lg': cardStyle === 'raised-2',
        'rounded-lg': cardStyle === 'high-contrast',
      },
      backgroundColorClass(blok.cardBgVariant),
      cardStyle,
    ]"
  >
    <div
      class="absolute inline-flex"
      :class="{
        'ml-3 -translate-y-1/2': cardStyle !== 'raised-2',
        '-right-6 mt-5 flex w-28 rotate-45 text-center':
          cardStyle === 'raised-2',
      }"
    >
      <span
        v-if="vacancy.topItem"
        class="py-0.5 text-sm"
        :class="{
          'rounded-full border border-green-500 bg-green-100 px-3 font-medium text-green-800':
            cardStyle !== 'raised-2',
          'bg-default w-full font-bold text-white uppercase':
            cardStyle === 'raised-2',
        }"
      >
        Top
      </span>
      <span
        v-else-if="publication && publication.scope === 'internal'"
        class="py-0.5 text-sm"
        :class="{
          'scope-tag rounded-full border px-3 font-medium':
            cardStyle !== 'raised-2',
          'bg-default w-full font-bold text-white uppercase':
            cardStyle === 'raised-2',
        }"
      >
        Interne
      </span>
      <span
        v-else-if="publication && publication.scope === 'regional'"
        class="py-0.5 text-sm"
        :class="{
          'scope-tag rounded-full border px-3 font-medium':
            cardStyle !== 'raised-2',
          'bg-default w-full font-bold text-white uppercase':
            cardStyle === 'raised-2',
        }"
      >
        Regionale
      </span>
    </div>

    <a
      v-if="vacancy"
      :href="vacancyLink"
      class="group flex h-full flex-col overflow-hidden focus:outline-hidden"
      :aria-labelledby="`title${vacancy.id} company${vacancy.id} description${vacancy.id}`"
      :class="{
        'pb-4': includedDetails.length,
        'pb-8': !includedDetails.length,
        'pt-6': cardStyle !== 'high-contrast',
      }"
    >
      <div
        class="flex grow flex-col md:flex-row"
        :class="{
          'md:items-center': showCompanyLogo && hideCompanyName,
        }"
      >
        <LogoBox
          v-if="showCompanyLogo"
          class="mb-4 ml-4 p-2 shadow-none md:mb-0"
          :options="logoOptions"
        />

        <div :class="cardStyle === 'high-contrast' ? 'w-full' : ''">
          <div
            class="company-name"
            :class="[
              showCompanyLogo ? 'pr-6 pl-4' : 'px-6',
              { 'rounded-t-lg pt-4 pb-3': cardStyle === 'high-contrast' },
            ]"
          >
            <p
              :id="`company${vacancy.id}`"
              class="font-medium"
              :class="[
                {
                  hidden:
                    hideCompanyName ||
                    !vacancy.company ||
                    !vacancy.company.title,
                  'mb-2': cardStyle !== 'high-contrast',
                  'overflow-hidden pr-20 text-ellipsis whitespace-nowrap':
                    cardStyle === 'high-contrast',
                },
                cardStyle === 'raised-2' ? 'text-base' : 'text-sm',
                isBackgroundDark(blok.cardBgVariant)
                  ? 'text-white'
                  : 'text-gray-800',
                ,
              ]"
            >
              {{ vacancy.company.title }}
            </p>
          </div>

          <div
            :id="`title${vacancy.id}`"
            class="grow text-2xl xl:text-xl"
            :class="[
              showCompanyLogo ? 'pr-6 pl-4' : 'px-6',
              textColorClass(
                blok.vacancyTitleVariant,
                blok.cardBgVariant,
                'default',
                'white',
              ),
              {
                'font-bold': cardStyle === 'raised-2',
                'group-hover:font-medium group-focus:font-medium':
                  cardStyle !== 'raised-2',
                'pt-4': cardStyle === 'high-contrast',
              },
            ]"
          >
            {{ vacancy.title }}
          </div>
        </div>
      </div>

      <div
        v-if="includedDetails.length"
        :id="`description${vacancy.id}`"
        class="mt-6 flex flex-wrap gap-x-5 gap-y-2 px-6 text-sm"
        :class="[
          {
            'border-t pt-4': !['raised', 'high-contrast'].includes(cardStyle),
            'pb-2': cardStyle === 'high-contrast',
          },
          isBackgroundDark(blok.cardBgVariant) ? 'text-white' : 'text-gray-700',
        ]"
      >
        <span
          v-if="vacancy.location?.city && includedDetails.includes('location')"
          class="vacancy-tag location flex items-center"
        >
          <MapPinIcon class="-mt-1 mr-1 inline-block h-5 w-5" />
          <span class="sr-only">plaats</span>
          <span>{{ vacancy.location.city }}</span>
        </span>
        <span
          v-if="hasEmployment && includedDetails.includes('duration')"
          class="vacancy-tag duration flex flex-nowrap items-center"
        >
          <ClockIcon class="-mt-0.5 mr-1 inline-block h-5 w-5" />
          <span class="sr-only">looptijd</span>
          <VacancySummaryDuration :employment="vacancy.employment" />
        </span>
        <span
          v-if="hasSalary && includedDetails.includes('salary')"
          class="vacancy-tag salary flex items-center"
        >
          <CurrencyEuroIcon class="-mt-0.5 mr-1 inline-block h-5 w-5" />
          <span class="sr-only">salaris</span>
          <VacancySummarySalary :salary="vacancy.salary" />
        </span>
        <span
          v-if="hasEducation && includedDetails.includes('education')"
          class="vacancy-tag education flex items-center"
        >
          <AcademicCapIcon class="-mt-1 mr-1 inline-block h-5 w-5" />
          <span class="sr-only">onderwijs</span>
          <VacancySummaryEducation :education="vacancy.education" />
        </span>
        <span
          v-if="
            publication &&
            publication.end &&
            includedDetails.includes('expiration') &&
            !hasMagicExpirationDate
          "
          class="vacancy-tag expiration flex items-center"
        >
          <CalendarDaysIcon class="-mt-1 mr-1 inline-block h-5 w-5" />
          <span class="sr-only">vervaldatum</span>
          <VacancySummaryExpiration :expiration-date="publication.end" />
        </span>
      </div>
    </a>

    <FavoriteButton
      v-if="showFavoriteButton"
      v-model="isFavorite"
      class="absolute top-0 right-0"
      :class="favoriteBtnStyle"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FavoriteButton from './FavoriteButton.vue'
import LogoBox from './LogoBox.vue'
import VacancySummaryDuration from './VacancySummaryDuration.vue'
import VacancySummarySalary from './VacancySummarySalary.vue'
import VacancySummaryEducation from './VacancySummaryEducation.vue'
import VacancySummaryExpiration from './VacancySummaryExpiration.vue'
import {
  AcademicCapIcon,
  CalendarDaysIcon,
  MapPinIcon,
} from '@heroicons/vue/20/solid'
import { ClockIcon, CurrencyEuroIcon } from '@heroicons/vue/24/outline'
import { useColorPalette } from '../composables/colorPalette'
import { getImgSrc } from '../utils/imgUtils'
import { pickPublicationScope } from '../utils/vacancyUtils.js'
import hydrateObject from '../utils/hydrateObject.js'
import { useMainStore } from '../store/index.js'
import { storeToRefs } from 'pinia'

const props = defineProps({
  vacancy: {
    type: Object,
    required: true,
  },
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const store = useMainStore()
const { favorites } = storeToRefs(store)
const {
  textColorClass,
  backgroundColorClass,
  isBackgroundDark,
  colorCustomClass,
} = useColorPalette()

const publication = pickPublicationScope(props.vacancy)
const hasSalary = computed(() => {
  return props.vacancy.salary?.min || props.vacancy.salary?.max
})
const hasEmployment = computed(() => {
  return (
    props.vacancy.employment?.hours?.min ||
    props.vacancy.employment?.hours?.max ||
    props.vacancy.employment?.hours?.exact
  )
})
const hasEducation = computed(() => {
  return props.vacancy.education?.length > 0
})
const cardStyle = computed(() => {
  return props.blok.style || 'raised'
})
const bgColorVariable = computed(() => {
  return `var(--${colorCustomClass(props.blok.cardBgVariant || 'white', null, '')})`
})
const hideCompanyName = computed(() => {
  return props.blok.hideCompanyName || false
})
const showCompanyLogo = computed(() => {
  return props.blok.showCompanyLogo || false
})
const showFavoriteButton = computed(() => {
  return props.blok.showFavoriteButton || false
})
const includedDetails = computed(() => {
  return props.blok.includedDetails || []
})
// hide expiration date on vacancies with magic date > 2999-12-01
const hasMagicExpirationDate = computed(() => {
  if (publication?.end) {
    const magicDate = new Date('2999-12-01T00:00')
    const expirationDate = new Date(publication.end)

    return expirationDate.valueOf() > magicDate.valueOf()
  }
  return false
})
const vacancyLink = computed(() => {
  // Convert something like: https://www.gemeentebanen.nl/vacatures/{{vacancy.id}}/{{vacancy.company.slug}}/{{vacancy.slug}}
  // to the real URL
  if (props.context.site.vacancyLinkFormat) {
    const context = { vacancy: props.vacancy }
    const { vacancyUrl } = hydrateObject(
      { vacancyUrl: props.context.site.vacancyLinkFormat },
      context,
    )

    const linkUrl = new URL(vacancyUrl)
    if (import.meta.env.VITE_IS_EDITOR) {
      return `/${props.context.domainSlug}${linkUrl.pathname}`
    } else {
      return linkUrl.pathname
    }
  } else {
    return `/${props.vacancy.id}`
  }
})
const logoOptions = computed(() => {
  return {
    image: props.vacancy.company?.logoUrl
      ? getImgSrc({
          imgUrl: props.vacancy.company.logoUrl,
          trim: 'color',
          trimcolor: 'fff',
          width: 100,
        })
      : null,
    title: props.vacancy.company.title,
    size: 100,
    hoverClass: 'group-hover:bg-gray-50',
  }
})
const focusColor = computed(() => {
  return `var(--${colorCustomClass(props.blok.vacancyTitleVariant || 'default', null, '')})`
})
const isFavorite = computed({
  get() {
    const index = favorites.value.findIndex(
      (favorite) => favorite.id === props.vacancy.id,
    )
    return index >= 0
  },
  set() {
    store.toggleFavorite(props.vacancy)
  },
})
const favoriteBtnStyle = computed(() => {
  let classes = cardStyle.value === 'raised-2' ? 'p-1' : 'p-3'
  if (cardStyle.value === 'high-contrast') {
    classes += ` ${backgroundColorClass(
      props.blok.favoriteBtnColor,
    )} text-white rounded-tr-lg`
  } else {
    classes += ` ${textColorClass(
      props.blok.favoriteBtnColor,
      props.blok.cardBgVariant,
      'default',
      'white',
    )}`
  }

  return classes
})
</script>

<style scoped>
@reference "../css/theme.css";

.vacancy-card:focus-within,
.vacancy-card:hover {
  border-color: v-bind(focusColor);
  background-color: color-mix(in oklab, v-bind(bgColorVariable), black 15%);
}

.scope-tag {
  border-color: var(--default);
  background-color: color-mix(in oklab, var(--default), white 90%);
  color: color-mix(in oklab, var(--default), black 25%);
}

.vacancy-card.high-contrast .company-name {
  background-color: color-mix(in oklab, v-bind(bgColorVariable), black 15%);
}

.vacancy-card.high-contrast .vacancy-tag {
  background-color: color-mix(in oklab, v-bind(bgColorVariable), white 85%);
  color: color-mix(in oklab, v-bind(bgColorVariable), black 10%);
  @apply rounded-md px-2 py-1;
}
</style>
